import { useParams } from "react-router-dom"

import {
  useCurrentUser,
  useFeatureFlag,
  useTopicRecordBulk,
  useTopics,
} from "web/hooks/data"
import { Dialogs, useDialogModal } from "web/hooks/modals"

import { ObjectList } from "web/components"
import useViewOrEditTopicModal from "web/components/ViewOrEdit/ViewOrEditTopic"
import routes from "web/routes/constants"

import { TrashIcon } from "@heroicons/react/24/outline"

const MAPPABLE_SUBJECT_TYPES = ["deal", "asset"]

function TopicObjectList({
  subjectType,
  topic,
  rootUrl,
  canRedirectToRootUrl,
}) {
  const currentUser = useCurrentUser()
  const topicRecordBulk = useTopicRecordBulk()
  const confirmDeleteModal = useDialogModal(
    Dialogs.ConfirmBulkDelete(`${topic.name} records`)
  )
  const useSSRTopicRecordModals = useFeatureFlag("ssr_topic_record_modals")

  const viewOrEditTopicModal = useViewOrEditTopicModal({
    subjectType,
    topic,
  })

  async function handleTopicRecordClick(record, list) {
    if (useSSRTopicRecordModals) {
      window.Turbo.visit(
        routes.ssrEditTopicRecord({
          subjectType: subjectType,
          subjectId: record.subject_id,
          topicName: topic.name,
          id: record.id,
          isCrossSubject: true,
        }),
        {
          frame: "modal",
        }
      )
    } else {
      await viewOrEditTopicModal({
        recording: record,
        isCrossSubject: true,
      })

      list.refresh()
    }
  }

  async function deleteSelectedRecords({ all, selected }) {
    const dialogResult = await confirmDeleteModal()
    if (dialogResult && dialogResult.response) {
      const ids = selected.map((topic_record) => topic_record.id)

      await topicRecordBulk.destroy({ ids })
      all.refresh()
    }
  }

  const subjectTypeIsMapable = MAPPABLE_SUBJECT_TYPES.includes(subjectType)
  const availableViews = subjectTypeIsMapable
    ? ["table", "compare", "map"]
    : ["table", "compare"]

  const selectedItemActions = []
  if (currentUser.is_super_user) {
    selectedItemActions.push({
      label: "Delete",
      icon: TrashIcon,
      action: deleteSelectedRecords,
    })
  }

  return (
    <ObjectList
      subjectType={subjectType}
      topic={topic}
      availableViews={availableViews}
      defaultView="table"
      enableLocationIndex={subjectTypeIsMapable}
      onItemClick={handleTopicRecordClick}
      selectedItemActions={selectedItemActions}
      rootUrl={rootUrl}
      canRedirectToRootUrl={canRedirectToRootUrl}
    />
  )
}

function SubjectTopicList({
  subjectType,
  topicName,
  rootUrl,
  canRedirectToRootUrl,
}) {
  const topics = useTopics({ subjectType })
  const topic = topics.find((topic) => topic.name === topicName)

  return (
    <TopicObjectList
      subjectType={subjectType}
      topic={topic}
      rootUrl={rootUrl}
      canRedirectToRootUrl={canRedirectToRootUrl}
    />
  )
}

function AssetTopicList({ topicName, rootUrl, canRedirectToRootUrl }) {
  const topics = useTopics({ subjectType: "asset" })
  const topic = topics.find((topic) => topic.name === topicName)

  return (
    <TopicObjectList
      subjectType="asset"
      topic={topic}
      rootUrl={rootUrl}
      canRedirectToRootUrl={canRedirectToRootUrl}
    />
  )
}

export default function GlobalTopicList({
  subjectType,
  route: routeProp,
  rootUrl,
  canRedirectToRootUrl,
}) {
  const routeParams = useParams()
  const topicName = !!routeProp ? routeProp[0].key : routeParams.topicName

  if (subjectType === "asset") {
    return (
      <AssetTopicList
        topicName={topicName}
        rootUrl={rootUrl}
        canRedirectToRootUrl={canRedirectToRootUrl}
      />
    )
  } else {
    return (
      <SubjectTopicList
        subjectType={subjectType}
        topicName={topicName}
        rootUrl={rootUrl}
        canRedirectToRootUrl={canRedirectToRootUrl}
      />
    )
  }
}
